// @ts-strict-ignore
import {getAddictionUnit, getRetentionBucketName} from 'common/report/retention/util';
import {formatMetricNumber, formatPercent} from 'common/util';
import {TooltipItem, TooltipItemType} from 'common/components/chart-tooltip/types';
import {FormattedRow, RetentionBookmarkParams, RetentionType} from 'common/report/retention/util/types';
import {getFrequencyTooltipSubtitle, isAddiction} from 'common/report/retention/util';
import {AGGREGATED_ROW_NAME, AGGREGATED_ROW_NAME_TRANSLATABLE} from 'common/report/retention/util/constants';
import {isSegmented} from 'common/report/retention/util/format';
import {TimeUnit, TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight';
import {formatLiftPercent} from 'common/report/util/lift';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {renderChartTooltipToString} from 'common/components/chart-tooltip/render-chart-tooltip-to-string';
import {getHighchartsColorString} from 'common/charts/visualization/line-chart/utils';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller';
import {LocalizationStore} from 'common/i18n/store';
import {MetricsResource} from 'common/report/model-resources/metrics';

import type {RetentionCurveChart} from './index';

// Axis label functions

export function retentionBucketLabel(bookmarkParams: RetentionBookmarkParams) {
  return function (value: number) {
    const {
      retention_type: retentionType,
      retention_unit: retentionUnit,
      date_unit: dateUnit,
      bucket_sizes: bucketSizes,
    } = bookmarkParams;

    return getRetentionBucketName({
      bucketIndex: value,
      retentionType,
      retentionUnit,
      dateUnit,
      bucketSizes,
    });
  };
}

export function frequencyBucketLabel(unit: string) {
  return function (value: number) {
    return `${value + 2} ${getAddictionUnit(unit)}s`;
  };
}

export function bucketLabeler(bookmarkParams: RetentionBookmarkParams) {
  return isAddiction(bookmarkParams)
    ? frequencyBucketLabel(bookmarkParams.date_unit)
    : retentionBucketLabel(bookmarkParams);
}

export function percentLabel(value: number) {
  return formatPercent(value, {precision: 0});
}

export function absoluteLabel(value: number) {
  return formatMetricNumber(value);
}

export function percentOrAbsoluteLabel(params: RetentionBookmarkParams) {
  if (params.lift_comparison) {
    return percentLabel;
  }
  return params.percentage_view ? percentLabel : absoluteLabel;
}

// Axis size functions
const MAX_X_TICKS = 20;
export function computeTicks(maxTicks = MAX_X_TICKS) {
  return function (min: number, max: number): number[] {
    const xTicks = [min];
    const skip = max - min + 1 > maxTicks ? Math.floor((max - min + 1) / maxTicks) : 0;
    while (min <= max) {
      if (min > xTicks[xTicks.length - 1] + skip) {
        xTicks.push(min);
      }
      min += 1;
    }
    return xTicks;
  };
}

// Chart description functions

// Tooltip generating functions
export function getLineChartTooltip(
  lineChart: RetentionCurveChart,
  metricsResource: MetricsResource,
  bookmarkController: RetentionBookmarkController,
) {
  const i18n = lineChart.props.i18n;
  return function (x: number, _y: number, series: any, _idx: number) {
    const row = lineChart.selectedSegmentsRows.find((segRow) => segRow.raw_head === series.userOptions.id);
    const liftData = lineChart.liftDataForSegment(row);
    const cell = row.data[x];

    const retentionType = bookmarkController.getRetentionType();
    const segments = bookmarkController.getSegments();
    const isAggregated = isAggregateRow(row, segments, retentionType);
    const {titleSection, subtitle} = getTitleAndSubtitle(bookmarkController, row, x, i18n);

    const retentionAddictionLabel = {
      text:
        bookmarkController.getRetentionType() === `addiction`
          ? i18n.t(`chart-actions.frequency.name`)
          : i18n.t(`chart-actions.retention.name`),
      type: TooltipItemType.Text,
    };

    const formatValue = bookmarkController.popoverFormatter(metricsResource, row.matching_show_clause_index);

    let bodySection;
    if (liftData) {
      const liftCell = liftData.liftRow.data[x];
      const baselineRow = liftData.baselineRow;
      const baselineCell = baselineRow?.data[x];

      let liftDisplayValue, currentSegmentReferenceValue, baselineSegmentReferenceValue;
      let isNegative = false;
      if (bookmarkController.isPercentageView(row.matching_show_clause_index)) {
        liftDisplayValue = liftCell.formatted_percent;
        isNegative = liftCell.percent < 0;
        currentSegmentReferenceValue = formatValue(cell.percent, true);
        baselineSegmentReferenceValue = formatValue(baselineCell.percent, true);
      } else {
        liftDisplayValue = formatLiftPercent(liftCell.value);
        isNegative = liftCell.value < 0;
        currentSegmentReferenceValue = formatValue(cell.value, false);
        baselineSegmentReferenceValue = formatValue(baselineCell.value, false);
      }

      bodySection = {
        rows: [
          {
            items: [
              {
                text: liftDisplayValue,
                type: TooltipItemType.Data,
                theme: isNegative ? TEXT_HIGHLIGHT_THEME.NEGATIVE : TEXT_HIGHLIGHT_THEME.POSITIVE,
              },
            ],
          },
          {
            items: [
              {text: currentSegmentReferenceValue, type: TooltipItemType.Data},
              ...(bookmarkController.isPercentageView(row.matching_show_clause_index)
                ? [retentionAddictionLabel]
                : [{}]),
              {text: row.display_head, type: TooltipItemType.Text},
            ],
          },
          ...(baselineRow && baselineRow.raw_head !== row.raw_head
            ? [
                {
                  items: [
                    {text: baselineSegmentReferenceValue, type: TooltipItemType.Data},
                    ...(bookmarkController.isPercentageView(row.matching_show_clause_index)
                      ? [retentionAddictionLabel]
                      : [{}]),
                    {text: baselineRow.display_head, type: TooltipItemType.Text},
                  ],
                },
              ]
            : []),
        ],
      };
    } else {
      const isPropertyAggregation = Boolean(bookmarkController.getPropertyAggregation(row.matching_show_clause_index));
      const getDataGroupLabel = () => {
        const dataGroupName = bookmarkController.getDataGroupName(false);
        if (isPropertyAggregation) {
          return ``;
        }

        if (dataGroupName === `user`) {
          return i18n.t(`chart-actions.tooltip.users`);
        }

        return i18n.t(`chart-actions.tooltip.maybe-plural-groups`, {group: dataGroupName});
      };

      const percentRow = {
        items: [{text: formatValue(cell.percent, true), type: TooltipItemType.Data}, retentionAddictionLabel],
      };
      const valueRow = {
        items: [
          {text: formatValue(cell.value, false), type: TooltipItemType.Data},
          {
            text: [getDataGroupLabel(), isAggregated ? `(${i18n.t(`chart-actions.retention.average`)})` : ``]
              .join(` `)
              .trim(),
            type: TooltipItemType.Text,
          },
        ],
      };
      const rows = isPropertyAggregation ? [valueRow] : [percentRow, valueRow];
      bodySection = {
        rows,
      };
    }
    const sections = [
      {
        rows: [
          {
            items: titleSection,
          },
          {
            items: [{text: subtitle, type: TooltipItemType.Detail}],
          },
        ],
      },
      bodySection,
    ];

    return renderChartTooltipToString({
      sections,
      titleColor: getHighchartsColorString(series.color),
      footerText: cell.incomplete
        ? i18n.t(`chart-actions.retention.incomplete-time-unit`, {
            timeUnit: i18n.print(TimeUnitLabelTranslatable[bookmarkController.getDateUnit()]?.(1) || ``),
          })
        : null,
    });
  };
}

function getTitleAndSubtitle(
  bookmarkController: RetentionBookmarkController,
  row: FormattedRow,
  x: number,
  i18n: LocalizationStore,
) {
  const matchingShowClauseIndex = row.matching_show_clause_index;
  const cell = row.data[x];
  const dateUnit = bookmarkController.getDateUnit();
  const segments = bookmarkController.getSegments();
  const retentionType = bookmarkController.getRetentionType(matchingShowClauseIndex);
  const retentionUnit = bookmarkController.getRetentionUnit(matchingShowClauseIndex);
  const bucketSizes = bookmarkController.getBucketSizes(matchingShowClauseIndex);
  const cumulative = bookmarkController.isCumulative(matchingShowClauseIndex);
  const isAggregatedRow = isAggregateRow(row, segments, retentionType);

  const title =
    retentionType === `addiction`
      ? getTitle(isSegmented(segments), isAggregatedRow, retentionType)
      : getRetentionBucketName({
          bucketIndex: x,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes,
        });

  const subtitle =
    retentionType === `addiction`
      ? getFrequencyTooltipSubtitle({
          cumulative,
          dateUnit: dateUnit as TimeUnit,
          bucketIndex: x,
        })
      : retentionTooltipSubtitle(isSegmented(segments), isAggregatedRow, retentionType, cell.incomplete);

  const titleSection: TooltipItem | TooltipItem[] = [{text: title, type: TooltipItemType.Strong}];

  return {subtitle, titleSection};

  function getTitle(segmented: boolean, isAggregated: boolean, type: RetentionType) {
    if (segmented) {
      return row.raw_head;
    } else if (isAggregated) {
      return i18n.print(AGGREGATED_ROW_NAME_TRANSLATABLE(type));
    } else {
      return i18n.t(`chart-actions.retention.users-entered-on`, {point: cell.entered_value});
    }
  }

  function retentionTooltipSubtitle(
    segmented: boolean,
    isAggregated: boolean,
    type: RetentionType,
    incomplete: boolean,
  ) {
    const isAggregatedAndComplete = isAggregated && !incomplete;

    return (
      getTitle(segmented, isAggregated, type) +
      (isAggregatedAndComplete ? ` ${i18n.t(`chart-actions.retention.in-completed-intervals`)}` : ``)
    );
  }
}

function isAggregateRow(row: FormattedRow, segments: Segment[], retentionType: RetentionType) {
  return isSegmented(segments) || row.display_head === AGGREGATED_ROW_NAME(retentionType);
}
